/**
 * Onboarding Form Settings — Phase 2 (Admin only)
 *
 * Backend:
 *   GET /api/onboarding-form/settings
 *   PUT /api/onboarding-form/settings  { completion_deadline_min, late_min, critical_min }
 *
 * Global configuration applied to ALL Onboarding Form (Master-Form follow-up)
 * tasks: the Completion Deadline (drives the Deadline column) and the Late /
 * Critical overdue thresholds (drive Overdue status, cards & calculations).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, AlertCircle, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const OnboardingFormSettings = () => {
  const [settings, setSettings] = useState({ completion_deadline_min: 1440, late_min: 60, critical_min: 180 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding-form/settings`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSettings(await res.json());
    } catch (e) { toast.error(e.message || 'Failed to load Onboarding Form settings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding-form/settings`, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          completion_deadline_min: parseInt(settings.completion_deadline_min, 10) || 0,
          late_min: parseInt(settings.late_min, 10) || 0,
          critical_min: parseInt(settings.critical_min, 10) || 0,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      setSettings(await res.json());
      toast.success('Onboarding Form settings saved');
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-form-settings-page">
      <div>
        <h3 className="text-lg font-bold text-gray-800 border-b pb-2">Onboarding Form Settings</h3>
        <p className="text-sm text-gray-500 mt-2">Global configuration for the Master-Form follow-up workflow. Applies to every Onboarding Form task.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList size={18} className="text-rose-600" />
          <h4 className="font-semibold text-gray-800">Completion Deadline</h4>
        </div>
        <NumberInput
          label="Completion Deadline (minutes from assignment)"
          value={settings.completion_deadline_min}
          onChange={(v) => setSettings({ ...settings, completion_deadline_min: v })}
          testId="of-completion-deadline"
        />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle size={18} className="text-amber-600" />
          <h4 className="font-semibold text-gray-800">Overdue Configuration (minutes past deadline)</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumberInput label="Late Threshold (min)" value={settings.late_min} onChange={(v) => setSettings({ ...settings, late_min: v })} testId="of-late-threshold" />
          <NumberInput label="Critical Threshold (min)" value={settings.critical_min} onChange={(v) => setSettings({ ...settings, critical_min: v })} testId="of-critical-threshold" />
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving || loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50"
          data-testid="of-settings-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Settings
        </button>
      </div>
    </div>
  );
};

const NumberInput = ({ label, value, onChange, testId }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
    <input type="number" min={0} value={value ?? ''} onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
      data-testid={testId} />
  </div>
);

export default OnboardingFormSettings;
